import {
  type BusinessUnitType,
  type PosExpectedSource,
  type SaveShiftClosing,
  saveShiftClosingSchema,
  type ShiftClosing,
  type ShiftClosingStatus,
  type ShiftClosingSummary,
  type ShiftClosingTotals,
  type ShiftMemberDuty,
  type ShiftTenderType,
  TRY_DENOMINATIONS,
} from "@station/contracts";
import type { Tone } from "@station/ui";
import { denominationPayload, denominationTotal, minorOf } from "../treasury/treasury-values.js";
import type { ParseResult } from "./record-values.js";

/**
 * Vardiya kapanış sihirbazının SAF dönüşümleri ve hesapları: ekran durumu ↔ `PUT /shifts/:id/closings/:memberId`
 * gövdesi, küpür toplamı, canlı beyan / fark önizlemesi, adım geçerliliği ve "sıradaki hesap" seçimi.
 *
 * Kurallar (AGENTS.md §3.2 ve `open-values.ts`'teki ders):
 *  · Para her yerde **kuruş cinsinden `bigint` / dizgedir**; hiçbir aşamada `number` float kullanılmaz.
 *  · **Boş satırlar ve EKRANDA GÖRÜNMEYEN koşullu alanlar gövdeye ÇIKMAZ** — doğrulama hatası
 *    kullanıcının göremediği bir alana düşemez. Somut olarak:
 *      – küpür paneli kapalıyken `cashDenominations` gönderilmez (toplam `cashCounted` olarak gider),
 *      – tutarı girilmemiş tahsilat satırı atılır (sunucu `amount > 0` ister),
 *      – `manualSales` YALNIZCA kasiyer hesabında ve beklenen tutarın kaynağı ELLE iken gönderilir: kasa
 *        satışı varsa (`expectedSource === "POS"`, ADR 0015) alan ekranda KİLİTLİDİR ve sunucu gövdeyi
 *        `400 pos_expected` ile reddeder,
 *      – `ON_ACCOUNT` / `FLEET` satırları HİÇ gönderilmez: onlar fişlerden türetilir (`SHIFT_TENDER_DERIVED`).
 */

// ── Adımlar ──────────────────────────────────────────────────────────────────
export const CLOSING_STEPS = ["expected", "cash", "card", "other", "summary"] as const;
export type ClosingStep = (typeof CLOSING_STEPS)[number];

export function nextClosingStep(step: ClosingStep): ClosingStep | null {
  const index = CLOSING_STEPS.indexOf(step);
  return CLOSING_STEPS[index + 1] ?? null;
}

export function previousClosingStep(step: ClosingStep): ClosingStep | null {
  const index = CLOSING_STEPS.indexOf(step);
  return index <= 0 ? null : CLOSING_STEPS[index - 1]!;
}

// ── Durum tonları ────────────────────────────────────────────────────────────
export const SHIFT_CLOSING_STATUS_TONE: Record<ShiftClosingStatus, Tone> = {
  DRAFT: "neutral",
  SUBMITTED: "info",
  PENDING_APPROVAL: "warning",
  ACCEPTED: "success",
  REJECTED: "danger",
};

/** Yalnızca giriliyor / reddedilmiş hesap değiştirilebilir (sunucudaki kuralla aynı). */
export function isClosingEditable(status: ShiftClosingStatus): boolean {
  return status === "DRAFT" || status === "REJECTED";
}

// ── Fark tonu ve yönü ────────────────────────────────────────────────────────
export type DifferenceDirection = "NONE" | "SHORTAGE" | "SURPLUS";

export function differenceDirection(difference: string | null | undefined): DifferenceDirection {
  const value = minorOf(difference);
  if (value === 0n) return "NONE";
  return value < 0n ? "SHORTAGE" : "SURPLUS";
}

/**
 * Fark rozetinin tonu. Renk TEK BAŞINA anlam taşımaz: çağıran ayrıca yön metnini ("Açık" / "Fazla") ve
 * işaretli tutarı yazar (WCAG 1.4.1). Tolerans DIŞI fazla da dikkat çekmelidir → sarı.
 */
export function differenceTone(difference: string | null | undefined, withinTolerance: boolean): Tone {
  const direction = differenceDirection(difference);
  if (direction === "NONE") return "success";
  if (direction === "SHORTAGE") return "danger";
  return withinTolerance ? "success" : "warning";
}

/**
 * İşaretli kuruş dizgesi: fazlada "+" yazılır ki yön yalnızca renkten okunmasın. DİKKAT: sonuç `MoneyText` / `Money.fromMinor`
 * girdisi DEĞİLDİR ("+10000" reddedilir → sayfa çöker); ekranda `SignedMoneyText` kullanılır.
 */
export function signedMinor(value: string | null | undefined): string {
  const amount = minorOf(value);
  return amount > 0n ? `+${amount.toString()}` : amount.toString();
}

// ── Form durumu ──────────────────────────────────────────────────────────────
/** İstemcinin GİREBİLECEĞİ tahsilat türleri: veresiye / filo türetildiği için listede yoktur. */
export const CLIENT_TENDER_TYPES = [
  "CARD",
  "VEHICLE_RECOGNITION",
  "MOBILE_PAYMENT",
  "COUPON",
  "LOYALTY_POINTS",
] as const satisfies readonly ShiftTenderType[];
export type ClientTenderType = (typeof CLIENT_TENDER_TYPES)[number];

/** Kart DIŞI araçlar (4. adım): TTS, mobil ödeme, kupon, puan. */
export const OTHER_TENDER_TYPES = [
  "VEHICLE_RECOGNITION",
  "MOBILE_PAYMENT",
  "COUPON",
  "LOYALTY_POINTS",
] as const satisfies readonly ClientTenderType[];

export interface TenderRow {
  /** Yalnızca ekranda yaşayan satır kimliği (React anahtarı ve hata bağlama için). */
  rowId: string;
  tenderType: ClientTenderType;
  /** Yalnızca `CARD` satırında anlamlıdır ("" → seçilmedi). */
  posTerminalId: string;
  /** Kuruş dizgesi. */
  amount: string | null;
  /** Serbest metin: boş ya da tam sayı (`spinbutton`). */
  slipCount: string;
  reference: string;
}

export interface ManualSaleRow {
  rowId: string;
  businessUnit: BusinessUnitType;
  revenueGroup: string;
  vatRateCode: string;
  /** KDV DÂHİL tutar, kuruş dizgesi. */
  gross: string | null;
}

export type DenominationQuantities = Record<string, number | null>;

export const EMPTY_DENOMINATIONS: DenominationQuantities = Object.fromEntries(
  TRY_DENOMINATIONS.map((value) => [value, null]),
);

export interface ClosingFormValues {
  /** İyimser kilit: sunucunun son verdiği sürüm. */
  version: number;
  /** Kapalıyken `cashDenominations` gövdeye ÇIKMAZ; toplam `cashCounted` olarak gider. */
  withDenominations: boolean;
  denominations: DenominationQuantities;
  cashCounted: string | null;
  /** 3. adım (POS): yalnızca `CARD` satırları. */
  cardRows: TenderRow[];
  /** 4. adım: TTS / mobil ödeme / kupon / puan. */
  otherRows: TenderRow[];
  /** Yalnızca KASİYER hesabında gönderilir. */
  manualSales: ManualSaleRow[];
  explanation: string;
}

export function emptyTenderRow(rowId: string, tenderType: ClientTenderType): TenderRow {
  return { rowId, tenderType, posTerminalId: "", amount: null, slipCount: "", reference: "" };
}

export function emptyManualSaleRow(rowId: string): ManualSaleRow {
  return { rowId, businessUnit: "MARKET", revenueGroup: "", vatRateCode: "", gross: null };
}

/**
 * Sunucudan gelen hesabı forma açar. Türetilmiş (`derived`) satırlar forma ALINMAZ: onlar salt okunur
 * gösterilir ve gövdeye hiç girmez.
 */
export function closingToForm(closing: ShiftClosing): ClosingFormValues {
  const counts: DenominationQuantities = { ...EMPTY_DENOMINATIONS };
  for (const row of closing.cashDenominations ?? []) counts[row.value] = row.quantity;
  const client = closing.tenders.filter((tender) => !tender.derived);
  const toRow = (tender: ShiftClosing["tenders"][number], index: number): TenderRow => ({
    rowId: tender.id || `t${index}`,
    tenderType: tender.tenderType as ClientTenderType,
    posTerminalId: tender.posTerminalId ?? "",
    amount: tender.amount,
    slipCount: tender.slipCount === null ? "" : String(tender.slipCount),
    reference: tender.reference ?? "",
  });
  return {
    version: closing.version,
    withDenominations: (closing.cashDenominations ?? []).length > 0,
    denominations: counts,
    cashCounted: closing.cashCounted,
    cardRows: client.filter((tender) => tender.tenderType === "CARD").map(toRow),
    otherRows: client.filter((tender) => tender.tenderType !== "CARD").map(toRow),
    manualSales: closing.expectedDetail.manualSales.map((line, index) => ({
      rowId: `ms${index}`,
      businessUnit: line.businessUnit,
      revenueGroup: line.revenueGroup,
      vatRateCode: line.vatRateCode,
      gross: line.gross,
    })),
    explanation: closing.explanation ?? "",
  };
}

// ── Toplamlar ve canlı önizleme ──────────────────────────────────────────────
/** Küpür sayımının toplamı (kuruş). Hazinedeki sayım ekranıyla AYNI yardımcıyı kullanır. */
export function countedTotal(quantities: Readonly<DenominationQuantities>): bigint {
  return denominationTotal(quantities);
}

/** Bir küpür satırının tutarı (kuruş): değer × adet. */
export function denominationRowTotal(value: string, quantity: number | null): bigint {
  if (quantity === null || !Number.isInteger(quantity) || quantity <= 0) return 0n;
  return minorOf(value) * BigInt(quantity);
}

/** Ekranda sayılan nakit: küpür paneli açıksa sayımdan, değilse elle girilen toplamdan. */
export function cashCountedOf(values: ClosingFormValues): bigint {
  return values.withDenominations ? countedTotal(values.denominations) : minorOf(values.cashCounted);
}

export interface DeclarationPreview {
  /** Satıştan gelen net nakit = sayılan − devir + ara teslim + nakitten masraf. */
  cashSales: bigint;
  nonCash: bigint;
  declared: bigint;
  /** beyan − beklenen (eksi = AÇIK). */
  difference: bigint;
}

/**
 * Beyanın CANLI önizlemesi (yapışkan alt çubuk). Kesin değer SUNUCUDAN gelir — bu hesap yalnızca
 * kullanıcı yazarken tutarı görsün diyedir ve sunucudaki formülün aynısıdır (`docs/domain/shifts.md`).
 */
export function declarationPreview(input: {
  cashCounted: bigint;
  openingFloat: string;
  cashDrops: string;
  cashExpenses: string;
  /** Sunucudan gelen TÜRETİLMİŞ satırlar (veresiye / filo). */
  derivedTenders: readonly { amount: string }[];
  clientRows: readonly TenderRow[];
  expectedAmount: string;
}): DeclarationPreview {
  const cashSales =
    input.cashCounted - minorOf(input.openingFloat) + minorOf(input.cashDrops) + minorOf(input.cashExpenses);
  const derived = input.derivedTenders.reduce<bigint>((total, row) => total + minorOf(row.amount), 0n);
  const client = input.clientRows.reduce<bigint>((total, row) => total + minorOf(row.amount), 0n);
  const nonCash = derived + client;
  const declared = cashSales + nonCash;
  return { cashSales, nonCash, declared, difference: declared - minorOf(input.expectedAmount) };
}

/** Kasiyerin elle satış özetinin toplamı = onun BEKLENEN tutarı (ADR 0013 karar 14). */
export function manualSalesTotal(rows: readonly ManualSaleRow[]): bigint {
  return rows.reduce<bigint>((total, row) => total + minorOf(row.gross), 0n);
}

export function tenderRowsTotal(rows: readonly TenderRow[]): bigint {
  return rows.reduce<bigint>((total, row) => total + minorOf(row.amount), 0n);
}

/**
 * Hesap özetlerinin alt toplamı. Sunucudaki `closingTotalsOf` ile BİREBİR aynı formüldür; ekran zaten
 * çektiği listeden hesaplar (hesap satırları `GET /shifts/:id/closings` çağrısıyla oluşur, vardiya
 * ayrıntısındaki `closingTotals` ise satırlar oluşmadan önce sıfır gelebilir).
 */
export function closingTotalsOf(rows: readonly ShiftClosingSummary[]): ShiftClosingTotals {
  let expected = 0n;
  let declared = 0n;
  let shortage = 0n;
  let surplus = 0n;
  let accepted = 0;
  for (const row of rows) {
    expected += minorOf(row.expectedAmount);
    declared += minorOf(row.declaredTotal);
    const difference = minorOf(row.difference);
    if (difference < 0n) shortage += -difference;
    else surplus += difference;
    if (row.status === "ACCEPTED") accepted += 1;
  }
  return {
    expectedAmount: expected.toString(),
    declaredTotal: declared.toString(),
    shortage: shortage.toString(),
    surplus: surplus.toString(),
    accepted,
    pending: rows.length - accepted,
  };
}

// ── Adım geçerliliği ─────────────────────────────────────────────────────────
export type ClosingStepProblem =
  | "cardTerminalRequired"
  | "cardAmountRequired"
  | "otherAmountRequired"
  | "manualSaleIncomplete"
  | "slipCountInvalid";

/**
 * Adımın "İleri"ye hazır olup olmadığı. Sunucunun doğrulamasını TEKRARLAMAZ; yalnızca kullanıcının
 * göremeyeceği bir hataya düşmesini engeller (yarım doldurulmuş satır).
 */
export function closingStepProblems(
  values: ClosingFormValues,
  step: ClosingStep,
  kind: ShiftMemberDuty,
  expectedSource: PosExpectedSource = "MANUAL",
): ClosingStepProblem[] {
  const problems: ClosingStepProblem[] = [];
  // Kasa kaynaklı beklenen tutarda satırlar SUNUCUNUNDUR: kullanıcı dolduramaz, eksik de olamaz.
  if (step === "expected" && kind === "CASHIER" && expectedSource === "MANUAL") {
    const incomplete = values.manualSales.some(
      (row) =>
        !isBlankManualSale(row) &&
        (row.revenueGroup.trim() === "" || row.vatRateCode === "" || minorOf(row.gross) <= 0n),
    );
    if (incomplete) problems.push("manualSaleIncomplete");
  }
  if (step === "card") {
    for (const row of values.cardRows) {
      if (isBlankTender(row)) continue;
      if (minorOf(row.amount) <= 0n) problems.push("cardAmountRequired");
      if (row.posTerminalId === "") problems.push("cardTerminalRequired");
      if (!isValidSlipCount(row.slipCount)) problems.push("slipCountInvalid");
    }
  }
  if (step === "other") {
    for (const row of values.otherRows) {
      if (isBlankTender(row)) continue;
      if (minorOf(row.amount) <= 0n) problems.push("otherAmountRequired");
      if (!isValidSlipCount(row.slipCount)) problems.push("slipCountInvalid");
    }
  }
  return [...new Set(problems)];
}

/** Hiç dokunulmamış satır: doğrulamadan ÖNCE elenir ve gövdeye çıkmaz. */
function isBlankTender(row: TenderRow): boolean {
  return (
    (row.amount === null || row.amount === "" || minorOf(row.amount) === 0n) &&
    row.posTerminalId === "" &&
    row.slipCount.trim() === "" &&
    row.reference.trim() === ""
  );
}

function isBlankManualSale(row: ManualSaleRow): boolean {
  return (
    row.revenueGroup.trim() === "" &&
    row.vatRateCode === "" &&
    (row.gross === null || row.gross === "" || minorOf(row.gross) === 0n)
  );
}

function isValidSlipCount(text: string): boolean {
  const trimmed = text.trim();
  return trimmed === "" || /^\d{1,6}$/.test(trimmed);
}

// ── Form → gövde ─────────────────────────────────────────────────────────────
function text(value: string): string | undefined {
  const trimmed = value.trim();
  return trimmed === "" ? undefined : trimmed;
}

function tenderBody(row: TenderRow): Record<string, unknown> {
  const body: Record<string, unknown> = {
    tenderType: row.tenderType,
    amount: minorOf(row.amount).toString(),
  };
  // POS terminali YALNIZCA kart satırında gönderilir (sunucu diğerlerinde `pos_not_allowed` der).
  if (row.tenderType === "CARD" && row.posTerminalId !== "") body.posTerminalId = row.posTerminalId;
  const slip = text(row.slipCount);
  if (slip !== undefined && /^\d{1,6}$/.test(slip)) body.slipCount = Number(slip);
  const reference = text(row.reference);
  if (reference !== undefined) body.reference = reference;
  return body;
}

export function saveClosingBody(
  values: ClosingFormValues,
  kind: ShiftMemberDuty,
  /** Kasiyer hesabında beklenenin kaynağı; `POS` ise elle satış özeti GÖNDERİLMEZ (ADR 0015). */
  expectedSource: PosExpectedSource = "MANUAL",
): unknown {
  const body: Record<string, unknown> = { version: values.version };

  if (values.withDenominations) {
    const rows = denominationPayload(values.denominations);
    // Panel açık ama hiç adet girilmemişse boş dizi göndermek yerine sayım "0" olarak yazılır.
    if (rows.length > 0) body.cashDenominations = rows;
    else body.cashCounted = "0";
  } else body.cashCounted = minorOf(values.cashCounted).toString();

  const tenders = [...values.cardRows, ...values.otherRows]
    .filter((row) => !isBlankTender(row))
    .map(tenderBody);
  if (tenders.length > 0) body.tenders = tenders;

  // Elle satış özeti YALNIZCA kasiyer hesabında anlamlıdır; pompacıda sunucu 400 verir. Beklenen tutar
  // kasadan geliyorsa (POS) satırlar sunucunun TÜRETTİĞİ satırlardır: geri gönderilirse 400 `pos_expected`.
  if (kind === "CASHIER" && expectedSource === "MANUAL") {
    const lines = values.manualSales
      .filter((row) => !isBlankManualSale(row))
      .map((row) => ({
        businessUnit: row.businessUnit,
        revenueGroup: row.revenueGroup.trim().toLocaleUpperCase("tr-TR"),
        vatRateCode: row.vatRateCode,
        gross: minorOf(row.gross).toString(),
      }));
    if (lines.length > 0) body.manualSales = lines;
  }

  const explanation = text(values.explanation);
  body.explanation = explanation ?? null;
  return body;
}

export function parseSaveClosing(
  values: ClosingFormValues,
  kind: ShiftMemberDuty,
  expectedSource: PosExpectedSource = "MANUAL",
): ParseResult<SaveShiftClosing> {
  const result = saveShiftClosingSchema.safeParse(saveClosingBody(values, kind, expectedSource));
  if (result.success) return { ok: true, body: result.data };
  return {
    ok: false,
    issues: result.error.issues.map((issue) => ({
      path: issue.path.join("."),
      message: issue.message,
    })),
  };
}

// ── Sıradaki hesap ───────────────────────────────────────────────────────────
/**
 * Aynı vardiyada TESLİM EDİLMEMİŞ bir sonraki üye: listedeki sıra korunur, şu anki hesaptan SONRAKİLER
 * öncelenir, yoksa baştan aranır (vardiya amiri kaldığı yerden devam etsin).
 */
export function nextPendingMemberId(
  rows: readonly ShiftClosingSummary[],
  currentMemberId: string,
): string | null {
  const pending = rows.filter((row) => isClosingEditable(row.status) && row.memberId !== currentMemberId);
  if (pending.length === 0) return null;
  const index = rows.findIndex((row) => row.memberId === currentMemberId);
  const after = pending.find((row) => rows.indexOf(row) > index);
  return (after ?? pending[0]!).memberId;
}
